/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Testimonials section - What Users Say About AI Image Enhancer
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Made My Photos Look Brand New',
    content:
      'I uploaded some old family pictures that were dark and blurry, and FalcoCut enhanced them beautifully. The details came back without looking fake, and the colors felt natural. It saved me hours I would have spent trying to fix them manually.',
    role: 'Freelance Photographer',
    name: 'Sarah Kim',
  },
  {
    title: 'Perfect for My Online Store',
    content:
      "Product photos are everything in e-commerce, and FalcoCut gave mine the sharpness and clarity they needed. My listings look more professional, and I've already noticed better engagement from customers.",
    role: 'E-Commerce Seller',
    name: 'Daniel Lopez',
  },
  {
    title: 'Helped Me Sell Properties Faster',
    content:
      "Real estate photos need to impress, and FalcoCut makes mine stand out. It brightens rooms, sharpens details, and gives listings a professional look that attracts buyers. It's a real advantage in my business.",
    role: 'Real Estate Agent',
    name: 'Michael Grant',
  },
  {
    title: 'Anime Frames Look Incredible',
    content:
      "I use FalcoCut to upscale anime artwork and the results are stunning. Lines are sharper, colors pop, and the images are ready for high-quality prints. It's now part of my daily workflow.",
    role: 'Digital Artist',
    name: 'Emily Johnson',
  },
  {
    title: 'Fast and Easy for Social Media',
    content:
      "I don't have time to edit every selfie or travel shot, but FalcoCut makes them look polished in seconds. My Instagram feed looks cleaner, and I get more likes without spending hours editing.",
    role: 'Content Creator',
    name: 'Marcus Reed',
  },
  {
    title: 'Exactly What My Design Projects Needed',
    content:
      "I often deal with low-res logos and graphics, and FalcoCut fixes them instantly. Everything looks crisp on websites and apps, saving me from re-creating assets from scratch. It's a huge time saver.",
    role: 'UX Designer',
    name: 'Tom Becker',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
